import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';

// Context
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Components
import Layout from './components/Layout';
// Debug console removed for production
import ErrorBoundary from './components/ErrorBoundary';
import NetworkStatus from './components/NetworkStatus';
import LandingPage from './pages/LandingPage';
import NotFound from './pages/NotFound';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import InvoiceForm from './pages/InvoiceForm';
import CompactInvoiceForm from './pages/CompactInvoiceForm';
import InvoiceList from './pages/InvoiceList';
import Profile from './pages/Profile';
import AdminDashboard from './pages/AdminDashboard';
import ReferenceDataManagement from './pages/ReferenceDataManagement';

import Reports from './pages/Reports';
import SupportChatPage from './pages/SupportChatPage';
import ScenarioInvoiceGenerator from './pages/ScenarioInvoiceGenerator';
import UoMChecker from './pages/UoMChecker';
import SupportPage from './pages/SupportPage';
import HelpCenterPage from './pages/HelpCenterPage';
import ContactUsPage from './pages/ContactUsPage';
import TrainingPage from './pages/TrainingPage';
import CommunityList from './pages/CommunityList';
import CommunityDetail from './pages/CommunityDetail';
import CanvasBuilderPage from './pages/CanvasBuilderPage';
import CanvasInvoiceForm from './pages/CanvasInvoiceForm';
import BulkCanvasProcessor from './components/BulkCanvasProcessor';
import BatchInvoices from './pages/BatchInvoices';
import BatchInvoiceDetails from './pages/BatchInvoiceDetails';
import FBRAPICheckerPage from './pages/FBRAPICheckerPage';
import SROChatPage from './pages/SROChatPage';
import AdminSROManagement from './pages/AdminSROManagement';
import SubscriptionRequestPage from './pages/SubscriptionRequestPage';
import SubscriptionHistory from './components/SubscriptionHistory';
import ProtectedPageRoute from './components/ProtectedPageRoute';
import MasterLogin from './pages/MasterLogin';
import MasterSignup from './pages/MasterSignup';
import MasterDashboard from './pages/MasterDashboard';
import MasterScenarios from './pages/MasterScenarios';
import MasterInvoices from './pages/MasterInvoices';
import MasterProfile from './pages/MasterProfile';
import MasterUsers from './pages/MasterUsers';
import BlogList from './pages/BlogList';
import BlogDetail from './pages/BlogDetail';
import BlogCategory from './pages/BlogCategory';
import BlogTag from './pages/BlogTag';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/login" />;
};

// Public Route Component (redirect to dashboard if authenticated)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return user ? <Navigate to="/app/dashboard" /> : children;
};

// Master User Protected Route
const MasterProtectedRoute = ({ children }) => {
  const masterToken = localStorage.getItem('masterToken');
  
  if (!masterToken) {
    return <Navigate to="/master/login" />;
  }
  
  return children;
};

// Root Component (shows landing page for non-authenticated users)
const RootComponent = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return user ? <Navigate to="/app/dashboard" /> : <LandingPage />;
};

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <Router
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true
            }}
          >
            <div className="App">
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
              }}
            />

            <Routes>
              {/* Master User Routes */}
              <Route path="/master/login" element={<MasterLogin />} />
              <Route path="/master/signup" element={<MasterSignup />} />
              <Route path="/master/dashboard" element={
                <MasterProtectedRoute>
                  <MasterDashboard />
                </MasterProtectedRoute>
              } />
              <Route path="/master/scenarios" element={
                <MasterProtectedRoute>
                  <MasterScenarios />
                </MasterProtectedRoute>
              } />
              <Route path="/master/invoices" element={
                <MasterProtectedRoute>
                  <MasterInvoices />
                </MasterProtectedRoute>
              } />
              <Route path="/master/profile" element={
                <MasterProtectedRoute>
                  <MasterProfile />
                </MasterProtectedRoute>
              } />
              <Route path="/master/users" element={
                <MasterProtectedRoute>
                  <MasterUsers />
                </MasterProtectedRoute>
              } />

              {/* Public Routes */}
              <Route path="/login" element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              } />
              <Route path="/register" element={
                <PublicRoute>
                  <Register />
                </PublicRoute>
              } />
              
              {/* Public Support Pages */}
              <Route path="/support" element={<SupportPage />} />
              <Route path="/help-center" element={<HelpCenterPage />} />
              <Route path="/contact-us" element={<ContactUsPage />} />
              <Route path="/training" element={<TrainingPage />} />
              <Route path="/community" element={<CommunityList />} />
              <Route path="/community/:id" element={<CommunityDetail />} />
              
              {/* Public Blog Pages */}
              <Route path="/blog" element={<BlogList />} />
              <Route path="/blog/:slug" element={<BlogDetail />} />
              <Route path="/blog/category/:slug" element={<BlogCategory />} />
              <Route path="/blog/tag/:slug" element={<BlogTag />} />
              
              {/* Public Landing (accessible even when logged in) */}
              <Route path="/home" element={<LandingPage />} />

              {/* Protected Routes */}
              <Route path="/app" element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }>
                <Route index element={<Navigate to="/app/dashboard" />} />
                <Route path="dashboard" element={<ProtectedPageRoute pageName="dashboard"><Dashboard /></ProtectedPageRoute>} />
                <Route path="admin" element={<ProtectedPageRoute pageName="admin_dashboard"><AdminDashboard /></ProtectedPageRoute>} />
                <Route path="invoice/new" element={<ProtectedPageRoute pageName="invoice_new"><InvoiceForm /></ProtectedPageRoute>} />
                <Route path="invoice/compact" element={<ProtectedPageRoute pageName="invoice_new"><CompactInvoiceForm /></ProtectedPageRoute>} />
                <Route path="invoice/edit/:id" element={<ProtectedPageRoute pageName="invoice_new"><InvoiceForm /></ProtectedPageRoute>} />
                <Route path="invoices" element={<ProtectedPageRoute pageName="invoice_list"><InvoiceList /></ProtectedPageRoute>} />
                <Route path="scenarios" element={<ProtectedPageRoute pageName="scenarios"><ScenarioInvoiceGenerator /></ProtectedPageRoute>} />
                <Route path="uom-checker" element={<ProtectedPageRoute pageName="uom_checker"><UoMChecker /></ProtectedPageRoute>} />
                <Route path="fbr-api-checker" element={<ProtectedPageRoute pageName="fbr_checker"><FBRAPICheckerPage /></ProtectedPageRoute>} />
                <Route path="reference-data" element={<ProtectedPageRoute pageName="reference_data"><ReferenceDataManagement /></ProtectedPageRoute>} />
                <Route path="reports" element={<ProtectedPageRoute pageName="reports"><Reports /></ProtectedPageRoute>} />
                <Route path="support" element={<ProtectedPageRoute pageName="support"><SupportChatPage /></ProtectedPageRoute>} />
                <Route path="sro-chat" element={<ProtectedPageRoute pageName="sro_chat"><SROChatPage /></ProtectedPageRoute>} />
                <Route path="sro-admin" element={<ProtectedPageRoute pageName="sro_admin"><AdminSROManagement /></ProtectedPageRoute>} />
                <Route path="subscription" element={<ProtectedPageRoute pageName="subscription"><SubscriptionRequestPage /></ProtectedPageRoute>} />
                <Route path="subscription-history" element={<ProtectedPageRoute pageName="subscription_history"><SubscriptionHistory /></ProtectedPageRoute>} />
                <Route path="profile" element={<ProtectedPageRoute pageName="profile"><Profile /></ProtectedPageRoute>} />
                <Route path="canvas" element={<ProtectedPageRoute pageName="canvas_builder"><CanvasBuilderPage /></ProtectedPageRoute>} />
                <Route path="canvas/:scenarioId" element={<ProtectedPageRoute pageName="canvas_builder"><CanvasBuilderPage /></ProtectedPageRoute>} />
                <Route path="invoice/canvas" element={<ProtectedPageRoute pageName="canvas_builder"><CanvasInvoiceForm /></ProtectedPageRoute>} />
                <Route path="bulk-upload" element={<ProtectedPageRoute pageName="bulk_upload"><BulkCanvasProcessor /></ProtectedPageRoute>} />
                <Route path="batch-invoices" element={<ProtectedPageRoute pageName="invoice_batch"><BatchInvoices /></ProtectedPageRoute>} />
                <Route path="batch-invoices/:batchId" element={<ProtectedPageRoute pageName="invoice_batch"><BatchInvoiceDetails /></ProtectedPageRoute>} />
              </Route>

              {/* Root route - landing page or dashboard based on auth status */}
              <Route path="/" element={<RootComponent />} />

              {/* Catch all route - redirect to login for unauthenticated users */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            
            {/* Debug Console removed for production */}
            
            {/* Network Status - only shows in development */}
            <NetworkStatus />
            </div>
          </Router>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
